import type { ProducttypeStatistics } from '@/api/topics/types'
import type { BarDatum } from '@/components/charts/BarList'
import type { LinePoint } from '@/components/charts/LineChart'
import { formatMonth } from '@/lib/format'

/**
 * Formt den Statistiküberblick der Weltmaschine für die Diagramm-Bausteine um.
 *
 * Reine Funktionen ohne React und ohne Netzwerk. Gerechnet wird nicht mehr
 * hier, das übernimmt der Statistikendpunkt. Diese Funktionen übersetzen
 * nur noch die Feldnamen des Backends in die Form, die BarList und
 * LineChart erwarten.
 */

/** Die stärksten Themen nach erreichbarem Suchvolumen. */
export function topTopicsByVolume(statistics: ProducttypeStatistics): readonly BarDatum[] {
  return statistics.topByPotential.map((entry) => ({
    label: entry.topic,
    value: entry.value,
    secondaryLabel: entry.producttypeName ?? entry.producttypeKey,
  }))
}

/** Die bestbewerteten Themen nach Ranking-Score. */
export function topTopicsByRanking(statistics: ProducttypeStatistics): readonly BarDatum[] {
  return statistics.topByRankingScore.map((entry) => ({
    label: entry.topic,
    value: entry.value,
    secondaryLabel: entry.producttypeName ?? entry.producttypeKey,
  }))
}

/** Wie sich die Themen auf die Taxonomie verteilen. */
export function distributionByCategory(statistics: ProducttypeStatistics): readonly BarDatum[] {
  return statistics.categoryDistribution
    .map((entry) => ({ label: entry.categoryLabel, value: entry.count }))
    .sort((a, b) => b.value - a.value)
}

/**
 * Verteilung über die Marge-Gruppen.
 *
 * Themen ohne Marge-Gruppe bekommen eine eigene Zeile statt still zu
 * verschwinden. Dass ein Produkttyp keiner Gruppe zugeordnet ist, ist
 * selbst eine Aussage.
 */
export function distributionByMarginGroup(statistics: ProducttypeStatistics): readonly BarDatum[] {
  return statistics.marginGroupDistribution
    .map((entry) => ({ label: entry.marginGroupLabel ?? 'Ohne Marge-Gruppe', value: entry.count }))
    .sort((a, b) => b.value - a.value)
}

/** Saisonverlauf über alle Themen. */
export function seasonality(statistics: ProducttypeStatistics): readonly LinePoint[] {
  return [...statistics.monthlyTrend]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((entry) => ({
      label: formatMonth(entry.year, entry.month),
      value: entry.searchVolume,
    }))
}
